import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Eye, Target, Sparkles, ArrowUpRight } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { Reveal, Stagger, StaggerItem } from "../components/Reveal";
import SectionHeading from "../components/SectionHeading";
import SEO from "../components/SEO";
import { IndustryIcon } from "../components/icons";
import { INDUSTRIES, ADVANTAGES } from "../data/content";

export default function Vision() {
  const { tr, lang, isAr } = useLang();

  return (
    <div data-testid="vision-page" className="pt-28 sm:pt-36">
      <SEO page="vision" lang={lang} />

      <section className="mx-auto max-w-7xl px-5 sm:px-8">
        <SectionHeading overline={tr.vision.badge} title={lang === "ar" ? "الرؤية والمهمة" : "Vision & Mission"} />
      </section>

      {/* Vision + Mission */}
      <section className="mx-auto max-w-7xl px-5 sm:px-8 py-14 sm:py-20">
        <div className="grid gap-6 lg:grid-cols-2">
          <Reveal variant="left">
            <div className="relative h-full overflow-hidden rounded-[2rem] bg-brand-blue p-9 sm:p-12 text-white">
              <div className="absolute -top-16 -right-16 h-52 w-52 rounded-full bg-white/10 blur-2xl" />
              <Eye className="h-9 w-9 text-white/80" />
              <h3 className="mt-6 text-2xl font-800">{tr.vision.visionTitle}</h3>
              <p className="mt-4 text-base sm:text-lg leading-relaxed text-white/80">{tr.vision.visionText}</p>
            </div>
          </Reveal>
          <Reveal variant="right">
            <div className="relative h-full overflow-hidden rounded-[2rem] border border-border bg-white p-9 sm:p-12">
              <div className="absolute -top-16 -right-16 h-52 w-52 rounded-full bg-brand-red/10 blur-2xl" />
              <Target className="h-9 w-9 text-brand-red" />
              <h3 className="mt-6 text-2xl font-800 text-ink">{tr.vision.missionTitle}</h3>
              <p className="mt-4 text-base sm:text-lg leading-relaxed text-slate">{tr.vision.missionText}</p>
            </div>
          </Reveal>
        </div>

        {/* Values */}
        <Stagger className="mt-6 grid gap-4 sm:grid-cols-2" stagger={0.12}>
          {tr.vision.values.map((v) => (
            <StaggerItem key={v} variant="up">
              <div className="flex items-center gap-4 rounded-2xl border border-border bg-surface p-6">
                <span className="grid h-12 w-12 place-items-center rounded-xl bg-gradient-to-br from-brand-blue to-brand-blue-light text-white">
                  <Sparkles className="h-6 w-6" />
                </span>
                <span className="text-lg font-700 text-ink">{v}</span>
              </div>
            </StaggerItem>
          ))}
        </Stagger>
      </section>

      {/* Industries */}
      <section className="bg-surface py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <SectionHeading overline={tr.vision.badge} title={tr.vision.indTitle} desc={tr.vision.indDesc} />
          <Stagger className="mt-14 grid gap-px overflow-hidden rounded-3xl border border-border bg-border sm:grid-cols-2 lg:grid-cols-3" stagger={0.06}>
            {INDUSTRIES.map((ind, i) => (
              <StaggerItem key={ind.id} variant="fade">
                <div className="group relative flex items-center gap-5 bg-white p-8 transition-colors hover:bg-brand-blue">
                  <span className="text-7xl font-900 leading-none text-surface-2 transition-colors group-hover:text-white/20">{String(i + 1).padStart(2, "0")}</span>
                  <div>
                    <span className="grid h-11 w-11 place-items-center rounded-xl bg-brand-blue/10 text-brand-blue transition-colors group-hover:bg-white/20 group-hover:text-white">
                      <IndustryIcon name={ind.icon} className="h-5 w-5" />
                    </span>
                    <h4 className="mt-3 text-base font-700 text-ink transition-colors group-hover:text-white">{isAr ? ind.ar : ind.en}</h4>
                  </div>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* The ICON advantage */}
      <section className="mx-auto max-w-7xl px-5 sm:px-8 py-24 sm:py-32">
        <SectionHeading overline={tr.vision.badge} title={tr.vision.advTitle} desc={tr.vision.advDesc} center />
        <Stagger className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4" stagger={0.05}>
          {ADVANTAGES.map((a) => (
            <StaggerItem key={a.icon} variant="up">
              <div className="group h-full rounded-2xl border border-border bg-white p-7 transition-all hover:-translate-y-1.5 hover:border-brand-blue hover:shadow-xl hover:shadow-brand-blue/10">
                <span className="grid h-12 w-12 place-items-center rounded-xl bg-brand-blue/8 text-brand-blue transition-colors group-hover:bg-brand-blue group-hover:text-white">
                  <IndustryIcon name={a.icon} className="h-6 w-6" />
                </span>
                <h4 className="mt-5 text-base font-700 text-ink">{a[lang].t}</h4>
                <p className="mt-2 text-sm leading-relaxed text-slate">{a[lang].d}</p>
              </div>
            </StaggerItem>
          ))}
        </Stagger>

        <Reveal variant="up" className="mt-14 flex justify-center">
          <Link to="/contact" className="group inline-flex items-center gap-2 rounded-full bg-brand-blue px-7 py-3.5 text-sm font-600 text-white transition-all hover:bg-brand-blue-dark">
            {tr.nav.cta}
            <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </Reveal>
      </section>
    </div>
  );
}
